import React, { useEffect, useState, useRef, useContext } from "react";
import "./Home.css";
import { TextField, Typography, Button, Box, Alert } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext"; // Import LanguageContext
import { FaAlignCenter, FaAlignJustify } from "react-icons/fa"; // These don't seem to be used, can be removed if not needed

export default function Home() {
  const images = [
    "/assets/home-banner1.jpg",
    "/assets/home-banner2.jpg",
    "/assets/home-banner3.jpg",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const slideshowRef = useRef(null);
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();
  const { isLoggedIn, setIsLoggedIn } = useContext(AuthContext);
  const { language } = useContext(LanguageContext); // Consume LanguageContext

  // --- Translatable Content ---
  const enContent = {
    welcome: "Welcome to ISSAD",
    tagline: "INSTITUTE OF SOCIAL SCIENCES & DEVELOPMENT",
    description:
      "The Institute plans to serve in the areas of Social Development, Educational Development, Economic Development, Psychological Rehabilitation and Health Promotions of the masses.",
    addPostButton: "Add Post",
    latestPosts: "Latest Posts",
    readOnlineButton: "Read Online",
    contactUsTitle: "Contact Us!",
    contactUsSubtitle: "Any questions or remarks? Just send us a message!",
    fullNameLabel: "Full Name",
    emailLabel: "Email",
    mobileNumberLabel: "Mobile Number",
    messageLabel: "Message",
    sendMessageButton: "Send Message",
    successMessage: "✅ Message sent successfully!",
    errorMessage: "Something went wrong.",
    submissionFailed: "Submission failed", // For error handling if needed
  };

  const urContent = {
    welcome: "اِصاد میں خوش آمدید", // Welcome to ISSAD
    tagline: "انسٹی ٹیوٹ آف سوشل سائنسز اینڈ ڈویلپمنٹ", // INSTITUTE OF SOCIAL SCIENCES & DEVELOPMENT
    description:
      "انسٹی ٹیوٹ کا منصوبہ ہے کہ وہ سماجی ترقی، تعلیمی ترقی، اقتصادی ترقی، نفسیاتی بحالی اور عوام کی صحت کے فروغ کے شعبوں میں خدمات انجام دے۔", // Translated description
    addPostButton: "پوسٹ شامل کریں", // Add Post
    latestPosts: "تازہ ترین پوسٹس", // Latest Posts
    readOnlineButton: "آن لائن پڑھیں", // Read Online
    contactUsTitle: "ہم سے رابطہ کریں!", // Contact Us!
    contactUsSubtitle: "کوئی سوال یا تبصرہ؟ ہمیں صرف ایک پیغام بھیجیں!", // Any questions or remarks? Just send us a message!
    fullNameLabel: "پورا نام", // Full Name
    emailLabel: "ای میل", // Email
    mobileNumberLabel: "موبائل نمبر", // Mobile Number
    messageLabel: "پیغام", // Message
    sendMessageButton: "پیغام بھیجیں", // Send Message
    successMessage: "✅ پیغام کامیابی سے بھیجا گیا!", // Message sent successfully!
    errorMessage: "کچھ غلط ہو گیا ہے۔", // Something went wrong.
    submissionFailed: "جمع کرانا ناکام ہو گیا", // Submission failed
  };

  const content = language === "en" ? enContent : urContent;
  // --- End Translatable Content ---

  // CONTACT FORM
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/messages/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || content.submissionFailed); // Use translated error message

      setSuccess(content.successMessage); // Use translated success message
      setFormData({ name: "", email: "", mobile: "", message: "" });
    } catch (err) {
      console.error(err);
      setError(err.message || content.errorMessage); // Use translated error message
    }
  };

  // LATEST POSTS
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/posts`);
        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error("Failed to fetch posts:", error);
        // Optionally set an error state here if post fetching fails
      }
    };

    fetchPosts();
  }, []);

  // SLIDESHOW DELAY
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        if (prev >= 2) {
          return 0;
        } else {
          return prev + 1;
        }
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // SLIDESHOW IMAGES
  useEffect(() => {
    const img = new Image();
    img.src = images[currentIndex];

    img.onload = () => {
      if (slideshowRef.current) {
        slideshowRef.current.style.backgroundImage = `url(${images[currentIndex]})`;
      }
    };
  }, [currentIndex, images]); // Added images to dependency array for correctness

  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <div ref={slideshowRef} className="slideshow"></div>
      <div className="home-container">
        <Typography
          className="frontpage-text"
          variant="h2"
          fontWeight="bold"
          data-tts="true"
          gutterBottom
        >
          {content.welcome}
        </Typography>
        <Typography variant="h5" className="frontpage-text" data-tts="true">
          {content.tagline}
        </Typography>
        <br />
        <Typography className="textarea" data-tts="true">
          {content.description}
        </Typography>

        <Box mt={5}>
          {isLoggedIn ? (
            <Button
              className="post-button"
              data-tts="true"
              variant="contained"
              onClick={() => navigate("/createContent")}
            >
              {content.addPostButton}
            </Button>
          ) : (
            ""
          )}
        </Box>

        <Box mt={10} px={2}>
          <Typography
            data-tts="true"
            variant="h4"
            className="frontpage-text"
            fontWeight="bold"
            gutterBottom
          >
            {content.latestPosts}
          </Typography>

          <Box
            className="scroll-container"
            sx={{
              display: "flex",
              overflowX: "auto",
              gap: 2,
              padding: "2rem",
              py: 2,
              maxWidth: "100vw",
            }}
          >
            {posts.map((post, index) => (
              <Box
                key={index}
                sx={{
                  minWidth: 280,
                  maxWidth: 280,
                  flex: "0 0 auto",
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                }}
              >
                {post.imageUrls.length > 0 ? (
                  <img
                    src={post.imageUrls[0]}
                    alt={post.title}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                      borderTopLeftRadius: "8px",
                      borderTopRightRadius: "8px",
                    }}
                  />
                ) : (
                  <Box p={5}></Box>
                )}
                <Box p={2}>
                  <Typography variant="h6" gutterBottom data-tts="true">
                    {post.title}
                  </Typography>
                  <Typography
                    data-tts="true"
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {post.subtitle}
                  </Typography>

                  <Button
                    className="custom-button"
                    data-tts="true"
                    variant="contained"
                    component={Link}
                    to={`/posts/${post._id}`}
                    sx={{ mt: 1 }}
                  >
                    {content.readOnlineButton}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </div>

      <Box mt={15} />

      <div id="contact" className="contact-area">
        <Typography variant="h3" className="frontpage-text" data-tts="true">
          {content.contactUsTitle}
        </Typography>
        <br />
        <Typography variant="h5" className="frontpage-text" data-tts="true">
          {content.contactUsSubtitle}
        </Typography>
        <form
          onSubmit={handleSubmit}
          style={{ marginTop: "2rem", maxWidth: "600px", width: "100%" }}
          className="contact-form"
        >
          <Box display="flex" flexDirection="column" gap={3}>
            <TextField
              data-tts="true"
              label={content.fullNameLabel}
              name="name"
              variant="outlined"
              required
              fullWidth
              value={formData.name}
              onChange={handleChange}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />

            <TextField
              data-tts="true"
              label={content.emailLabel}
              name="email"
              type="email"
              variant="outlined"
              required
              fullWidth
              value={formData.email}
              onChange={handleChange}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />

            <TextField
              data-tts="true"
              label={content.mobileNumberLabel}
              name="mobile"
              type="tel"
              variant="outlined"
              required
              fullWidth
              value={formData.mobile}
              onChange={handleChange}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />

            <TextField
              data-tts="true"
              label={content.messageLabel}
              name="message"
              multiline
              rows={4}
              variant="outlined"
              required
              fullWidth
              value={formData.message}
              onChange={handleChange}
              sx={{
                backgroundColor: "#f9f9f9",
                borderRadius: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            />

            <Box display={"flex"} justifyContent={"center"}>
              <Button
                data-tts="true"
                variant="contained"
                type="submit"
                className="submit-button"
              >
                {content.sendMessageButton}
              </Button>
            </Box>
          </Box>
        </form>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        <br />
      </div>
    </div>
  );
}
